import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useLang } from '../i18n/LangContext';
import Reveal from './Reveal';
import { IMG } from '../data/realImages';

// Use a real iconic Alpha7 construction project as hero (San Andrés Cholula tower)
const HERO_BG = '/images/hero-cholula.jpg';

export default function Hero() {
  const { t, lang } = useLang();

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const marqueeItems = t('marquee');

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="ALPHA 7 industrial construction project"
          className="w-full h-full object-cover"
          style={{ objectPosition: 'center 35%' }}
          loading="eager"
        />
        {/* Multi-layer cinematic gradient for legibility and atmosphere */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-[#0A0A0B]/80 to-[#0A0A0B]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-[#0A0A0B]/50" />
        <div className="absolute inset-0 bg-[#0A0A0B]/25" />
      </div>

      {/* Stat ticker — 450+ projects */}
      <div className="absolute top-[110px] left-6 md:left-12 hidden md:flex items-center gap-3 font-mono text-[10px] tracking-[0.3em] uppercase text-[#A1A1AA] z-10">
        <span className="w-1.5 h-1.5 bg-[#C8102E]" />
        {t('hero.ticker')}
      </div>

      {/* Decorative crosshair grid corners */}
      <div className="absolute top-[110px] right-12 hidden md:flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-[#A1A1AA] z-10">
        <span className="w-2 h-[1px] bg-[#C8102E]" />
        N — 19°&nbsp;{lang === 'es' ? 'LAT' : 'LAT'}
      </div>
      <div className="absolute bottom-12 right-12 hidden md:flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-[#A1A1AA] z-10">
        ISO — INDUSTRIAL ENGINEERING
        <span className="w-2 h-[1px] bg-[#C8102E]" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 pt-[140px] md:pt-[180px] pb-24">
        <Reveal className="overline mb-6">{t('hero.eyebrow')}</Reveal>

        <Reveal className="hero-title text-[#F5F5F2] max-w-[1100px] reveal-delay-1">
          {t('hero.title_pre')}{' '}
          <em>{t('hero.title_accent')}</em>{' '}
          {t('hero.title_post')}
        </Reveal>

        <Reveal className="reveal-delay-2 mt-6 inline-flex items-center gap-3">
          <span className="w-6 h-[1px] bg-[#C8102E]" />
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#A1A1AA]">
            ALPHA 7 · Soluciones y Proyectos
          </span>
        </Reveal>

        <Reveal className="mt-8 max-w-2xl text-base md:text-lg text-[#A1A1AA] leading-relaxed reveal-delay-2">
          {t('hero.sub')}
        </Reveal>

        <Reveal className="mt-12 flex flex-col sm:flex-row gap-4 reveal-delay-3">
          <button
            data-testid="hero-explore-button"
            onClick={() => scrollTo('#divisions')}
            className="btn-primary"
          >
            {t('hero.btn_primary')}
            <ArrowRight className="arrow" size={16} />
          </button>
          <button
            data-testid="hero-partner-button"
            onClick={() => scrollTo('#contact')}
            className="btn-secondary"
          >
            {t('hero.btn_secondary')}
            <ArrowRight className="arrow" size={16} />
          </button>
        </Reveal>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-6 md:left-12 z-10 flex items-center gap-3">
        <div className="w-[1px] h-12 bg-[#27272A] relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-3 bg-[#C8102E] scroll-indicator-dot" />
        </div>
        <span className="font-mono text-[10px] tracking-[0.3em] text-[#A1A1AA]">
          {t('hero.scroll')}
        </span>
      </div>

      {/* Bottom marquee */}
      <div className="absolute bottom-0 left-0 right-0 border-t border-[#27272A] bg-[#0A0A0B]/80 backdrop-blur-sm z-10">
        <div className="overflow-hidden">
          <div className="marquee-track flex gap-12 py-4 whitespace-nowrap">
            {[...marqueeItems, ...marqueeItems, ...marqueeItems].map((item, i) => (
              <span key={i} className="font-mono text-[11px] tracking-[0.24em] uppercase text-[#A1A1AA] flex items-center gap-12">
                <span className="w-1 h-1 bg-[#C8102E] rounded-none" />
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
